import { useState, useEffect, useCallback, useContext } from "react";
import { useHttpClient } from "./http-hook";
import { AuthContext } from "../context/auth-context";

/**
 * Custom hook for translation functionality
 * @returns {Object} Translation state and functions
 */
export const useTranslator = () => {
  const { error, sendRequest, clearError } = useHttpClient();
  const authContext = useContext(AuthContext);
  
  // State
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [showTranslationModal, setShowTranslationModal] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  // Fetch available languages
  const fetchLanguages = useCallback(async () => {
    try {
      const response = await sendRequest(
        "/api/translation/languages",
        "GET",
        null,
        {
          Authorization: "Bearer " + authContext.token,
        }
      );
      setAvailableLanguages(response.data || []);
    } catch (err) {
      console.error("Error fetching languages:", err);
    }
  }, [sendRequest, authContext.token]);

  // Load languages on mount
  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  // Handle translation
  const handleTranslate = async (text) => {
    if (!selectedLanguage || !text) return;
    
    setIsTranslating(true);
    try {
      const response = await sendRequest(
        "/api/translation/translate",
        "POST",
        JSON.stringify({
          text: text,
          targetLanguage: selectedLanguage,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + authContext.token,
        }
      );
      
      // The API returns a response with the structure:
      // { originalText, translatedText, targetLanguage, organization }
      setTranslatedText(response.data.translatedText || "");
      setShowTranslationModal(true);
    } catch (err) {
      console.error("Translation error:", err);
    } finally {
      setIsTranslating(false);
    }
  };

  // Reset state
  const resetTranslation = () => {
    setTranslatedText("");
    setShowTranslationModal(false);
  };

  // Close modal
  const closeModal = () => {
    setShowTranslationModal(false);
  };

  return {
    // State
    availableLanguages,
    selectedLanguage,
    translatedText,
    showTranslationModal,
    isTranslating,
    error,
    
    // Functions
    setSelectedLanguage,
    handleTranslate,
    resetTranslation,
    closeModal,
    clearError,
  };
};
